import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  createAnnouncement,
  getAnnouncements,
  getAnnouncementById,
  updateAnnouncement,
  deleteAnnouncement,
} from '@app/api/announcements.api';
import { Announcement, CreateAnnouncementDto, UpdateAnnouncementDto, AnnouncementFilters, AnnouncementPaginatedResponse } from '@app/types/api.types';

export interface AnnouncementsState {
  items: Announcement[];
  currentItem: Announcement | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  filters: AnnouncementFilters;
  loading: boolean;
  error: string | null;
}

const initialState: AnnouncementsState = {
  items: [],
  currentItem: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  },
  filters: {
    page: 1,
    limit: 10,
  },
  loading: false,
  error: null,
};

export const fetchAnnouncements = createAsyncThunk(
  'announcements/fetchAnnouncements',
  async (filters: AnnouncementFilters = { page: 1, limit: 10 }, { rejectWithValue }) => {
    try {
      const response = await getAnnouncements(filters);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch announcements');
    }
  }
);

export const fetchAnnouncementById = createAsyncThunk(
  'announcements/fetchAnnouncementById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await getAnnouncementById(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch announcement');
    }
  }
);

export const createAnnouncementAction = createAsyncThunk(
  'announcements/createAnnouncement',
  async (data: CreateAnnouncementDto, { rejectWithValue }) => {
    try {
      const response = await createAnnouncement(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create announcement');
    }
  }
);

export const updateAnnouncementAction = createAsyncThunk(
  'announcements/updateAnnouncement',
  async ({ id, data }: { id: string; data: UpdateAnnouncementDto }, { rejectWithValue }) => {
    try {
      const response = await updateAnnouncement(id, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update announcement');
    }
  }
);

export const deleteAnnouncementAction = createAsyncThunk(
  'announcements/deleteAnnouncement',
  async (id: string, { rejectWithValue }) => {
    try {
      await deleteAnnouncement(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete announcement');
    }
  }
);

const announcementsSlice = createSlice({
  name: 'announcements',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<AnnouncementFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearCurrentItem: (state) => {
      state.currentItem = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Announcements
    builder.addCase(fetchAnnouncements.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchAnnouncements.fulfilled, (state, action) => {
      state.loading = false;
      state.items = action.payload.data;
      state.pagination = {
        page: action.payload.page,
        limit: action.payload.limit,
        total: action.payload.total,
        totalPages: action.payload.totalPages || Math.ceil(action.payload.total / action.payload.limit),
        hasNext: action.payload.hasNext || action.payload.page < (action.payload.totalPages || Math.ceil(action.payload.total / action.payload.limit)),
        hasPrev: action.payload.hasPrev || action.payload.page > 1,
      };
    });
    builder.addCase(fetchAnnouncements.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch Announcement By ID
    builder.addCase(fetchAnnouncementById.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchAnnouncementById.fulfilled, (state, action) => {
      state.loading = false;
      state.currentItem = action.payload;
    });
    builder.addCase(fetchAnnouncementById.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Create Announcement
    builder.addCase(createAnnouncementAction.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(createAnnouncementAction.fulfilled, (state, action) => {
      state.loading = false;
      state.items.unshift(action.payload);
    });
    builder.addCase(createAnnouncementAction.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Update Announcement
    builder.addCase(updateAnnouncementAction.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(updateAnnouncementAction.fulfilled, (state, action) => {
      state.loading = false;
      const index = state.items.findIndex((item) => item.id === action.payload.id);
      if (index !== -1) {
        state.items[index] = action.payload;
      }
      if (state.currentItem?.id === action.payload.id) {
        state.currentItem = action.payload;
      }
    });
    builder.addCase(updateAnnouncementAction.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Delete Announcement
    builder.addCase(deleteAnnouncementAction.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(deleteAnnouncementAction.fulfilled, (state, action) => {
      state.loading = false;
      state.items = state.items.filter((item) => item.id !== action.payload);
      if (state.currentItem?.id === action.payload) {
        state.currentItem = null;
      }
    });
    builder.addCase(deleteAnnouncementAction.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
  },
});

export const { setFilters, clearCurrentItem } = announcementsSlice.actions;
export default announcementsSlice.reducer;

